import { HTMLBnumSegmentedItem } from '../bnum-segmented-item.part';
import { Listen } from '../../../../core/decorators/listen';
import BnumElementInternal from '../../../bnum-element-states';
import { Data, Define, Fire, SetAttr } from '../../../../core/decorators';
import { Nullable } from '@rotomeca/utils';
import Log from '../../../../core/utils/Log';
import style from './bnum-segmented-control.less';
import { TAG_SEGMENTED_CONTROL } from 'core/utils/constants/tags';

//#region Global constants
const SELECTOR_SELECTED = '[selected]';
const STATE_NO_LEGEND = 'no-legend';
const EVENT_CHANGE = 'bnum-segmented-control:change';
const EVENT_ERROR = 'bnum-segmented-control:error';
const TAG_SEGMENTED_ITEM = HTMLBnumSegmentedItem.TAG;

/**
 * Énumération des événements émis par le contrôle segmenté.
 *
 * @remarks
 * - `CHANGE` : déclenché lors de la sélection d'un item
 * - `ERROR` : déclenché en cas d'erreur interne
 *
 * @example
 * ```typescript
 * control.addEventListener(
 *   HTMLBnumSegmentedControl.Events.CHANGE,
 *   (e) => console.log('Sélection:', e.detail.value)
 * );
 * ```
 */
const EVENTS = {
	CHANGE: EVENT_CHANGE,
	ERROR: EVENT_ERROR,
} as const;
//#endregion Global constants

//#region Template
const TEMPLATE = (
	<div id="container">
		<label class="bnum-segmented-control__legend">
			<slot></slot>
		</label>
		<div class="bnum-segmented-control__items">
			{' '}
			<slot name="items"></slot>
		</div>
	</div>
);
//#endregion Template

/**
 * Élément de contrôle segmenté (groupe de boutons radio).
 *
 * @category SegmentedControl
 *
 * @structure Sans icône
 * <bnum-segmented-control>
 *   Légende du contrôle
 *  <bnum-segmented-item slot="items" value="option1">Option 1</bnum-segmented-item>
 *  <bnum-segmented-item slot="items" value="option2">Option 2</bnum-segmented-item>
 * </bnum-segmented-control>
 *
 * @structure Avec icône
 * <bnum-segmented-control>
 *   Légende du contrôle
 *  <bnum-segmented-item slot="items" data-icon="add" value="option1">Option 1</bnum-segmented-item>
 *  <bnum-segmented-item slot="items" data-icon="remove" value="option2">Option 2</bnum-segmented-item>
 * </bnum-segmented-control>
 *
 * @structure Avec 3 segments
 * <bnum-segmented-control>
 *   Légende du contrôle
 *  <bnum-segmented-item slot="items" data-icon="view_agenda" value="option1">Option 1</bnum-segmented-item>
 *  <bnum-segmented-item slot="items" data-icon="view_array" value="option2">Option 2</bnum-segmented-item>
 *  <bnum-segmented-item slot="items" data-icon="view_carousel" value="option3">Option 3</bnum-segmented-item>
 * </bnum-segmented-control>
 *
 * @description
 * Composant permettant de présenter plusieurs options mutuellement exclusives
 * sous la forme d'un groupe de contrôles segmentés.
 *
 * @remarks
 * - Respecte le pattern WAI-ARIA "Radio Group"
 * - Support complet de la navigation au clavier (flèches, boucle cyclique)
 * - Support de la directionnalité RTL (arabe, hébreu, etc.)
 * - La sélection suit le focus pour l'accessibilité
 *
 * @example
 * ```html
 * <bnum-segmented-control>
 *    Choisir une option
 *    <bnum-segmented-item slot="items" selected value="opt1">Option 1</bnum-segmented-item>
 *    <bnum-segmented-item slot="items" value="opt2">Option 2</bnum-segmented-item>
 * </bnum-segmented-control>
 * ```
 *
 * @example
 * ```typescript
 * const control = HTMLBnumSegmentedControl.Create('Ma légende');
 * control.addEventListener('bnum-segmented-control:change', (e) => {
 *   console.log('Valeur sélectionnée:', e.detail.value);
 * });
 * ```
 *
 * @event {CustomEvent} bnum-segmented-control:change - Émis lors de la sélection d'un item. Détail : `{value: string, item: HTMLBnumSegmentedItem, caller: HTMLBnumSegmentedControl}`
 * @event {CustomEvent} bnum-segmented-control:error - Émis en cas d'erreur interne. Détail : `{error: Error, caller: HTMLBnumSegmentedControl}`
 *
 * @see {@link HTMLBnumSegmentedItem}
 */
@Define({
	tag: TAG_SEGMENTED_CONTROL,
	styles: style,
	template: TEMPLATE,
})
export class HTMLBnumSegmentedControl extends BnumElementInternal {
	//#region Getters/Setters
	/**
	 * Contrôle l'affichage de la légende du contrôle segmenté.
	 *
	 * @remarks
	 * - `true` : affiche la légende (défaut)
	 * - `false` : masque la légende via la classe `no-legend`
	 *
	 * @decorator `@Data()`
	 * @default true
	 */
	@Data() accessor #_legend: boolean = true;

	/**
	 * Récupère l'élément actuellement sélectionné dans le contrôle.
	 *
	 * @remarks
	 * Retourne `null` si aucun élément n'est sélectionné.
	 *
	 * @readonly
	 * @returns {Nullable<HTMLBnumSegmentedItem>} L'item sélectionné ou null.
	 */
	get selected(): Nullable<HTMLBnumSegmentedItem> {
		return this.querySelector(SELECTOR_SELECTED);
	}

	/**
	 * Récupère la valeur de l'élément actuellement sélectionné.
	 *
	 * @remarks
	 * La valeur provient de l'attribut `value` de l'item sélectionné.
	 * Retourne `null` si aucun élément n'est sélectionné.
	 *
	 * @readonly
	 * @returns {Nullable<string>} La valeur de l'item sélectionné ou null.
	 *
	 * @example
	 * ```typescript
	 * const control = document.querySelector('bnum-segmented-control');
	 * console.log(control.currentValue); // "option1"
	 * ```
	 */
	get currentValue(): Nullable<string> {
		return this.selected?.value || null;
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	/**
	 * Initialise le DOM et configure le composant.
	 *
	 * @remarks
	 * Définit l'attribut `role="radiogroup"` et initialise :
	 * - L'affichage/masquage de la légende
	 * - Les écouteurs d'événements clavier
	 * - Les écouteurs de sélection d'items
	 *
	 * @protected
	 * @decorator `@SetAttr('role', 'radiogroup')`
	 */
	@SetAttr('role', 'radiogroup')
	protected _p_buildDOM(): void {
		this.#_initLegendVisibility().#_setListeners().#_initSelectedItem();
	}

	//#endregion Lifecycle

	//#region Private methods
	/**
	 * Gère l'affichage de la légende en fonction de l'attribut `data-legend`.
	 *
	 * @remarks
	 * Si `legend` est `false`, ajoute l'état CSS `no-legend` au composant.
	 *
	 * @private
	 * @returns L'instance courante pour le chaînage de méthodes.
	 */
	#_initLegendVisibility(): this {
		if (!(this.#_legend ?? true)) this._p_addState(STATE_NO_LEGEND);
		return this;
	}

	/**
   * Initialise la sélection du premier item par défaut.
   *
   * @description
   * Si aucun item n'est déjà sélectionné, sélectionne automatiquement
   * le premier item du contrôle segmenté.
   *
   * @remarks
   * - Garantit qu'au moins un item est toujours sélectionné
   * - Déclenche l'événement `bnum-segmented-control:change` via `click()`
   * - Utile pour initialiser le state du composant au chargement

   * @returns L'instance courante pour le chaînage de méthodes.
   */
	#_initSelectedItem(): this {
		if (!this.selected) {
			const firstItem = this.querySelector(
				TAG_SEGMENTED_ITEM,
			) as Nullable<HTMLBnumSegmentedItem>;
			if (firstItem) firstItem.click();
		}

		return this;
	}

	/**
	 * Enregistre les écouteurs d'événements du composant.
	 *
	 * @remarks
	 * Configure :
	 * - La navigation au clavier (flèches, boucle cyclique)
	 * - La sélection d'items
	 *
	 * @private
	 * @returns L'instance courante pour le chaînage de méthodes.
	 */
	#_setListeners(): this {
		this.#_onItemSelected();
		this.#_handleKeyboardNavigation();
		return this;
	}

	/**
	 * Gère la navigation au clavier au sein du groupe (accessibilité).
	 *
	 * @description
	 * Intercepte les touches fléchées pour déplacer le focus et la sélection :
	 * - **Flèche bas / Flèche droite (LTR)** : sélectionne l'item suivant
	 * - **Flèche haut / Flèche gauche (LTR)** : sélectionne l'item précédent
	 * - **Support RTL** : les flèches droite/gauche sont inversées
	 * - **Comportement cyclique** : la dernière option ramène à la première
	 *
	 * @remarks
	 * Respecte le pattern WAI-ARIA "Radio Group" où la sélection suit le focus.
	 * Prévient le scroll natif de la page lors de la navigation.
	 *
	 * @private
	 * @decorator `@Listen('keydown')`
	 * @returns Fonction de gestion de l'événement clavier.
	 */
	@Listen('keydown')
	#_handleKeyboardNavigation(): Function {
		return (e: KeyboardEvent) => {
			// Définition des touches de navigation
			// Support RTL : si la page est en arabe/hébreu, Gauche/Droite sont inversés
			const isRTL = getComputedStyle(this).direction === 'rtl';
			const KEY_NEXT = ['ArrowDown', isRTL ? 'ArrowLeft' : 'ArrowRight'];
			const KEY_PREV = ['ArrowUp', isRTL ? 'ArrowRight' : 'ArrowLeft'];
			const ALL_KEYS = [...KEY_NEXT, ...KEY_PREV];

			// Ignorer les touches non concernées
			if (!ALL_KEYS.includes(e.key)) return;

			// Prévenir le scroll natif de la page
			e.preventDefault();

			// Récupération des items
			const items = Array.from(
				this.querySelectorAll(TAG_SEGMENTED_ITEM),
			) as HTMLBnumSegmentedItem[];

			if (items.length === 0) return;

			// Calcul du nouvel index (Logique cyclique)
			const currentItem = this.selected as HTMLBnumSegmentedItem;
			// Si rien n'est sélectionné, on part de -1 (donc next sera 0)
			const currentIndex = currentItem ? items.indexOf(currentItem) : -1;
			const direction = KEY_PREV.includes(e.key) ? -1 : 1;

			// Formule mathématique pour le "wrap-around" (boucle fin -> début)
			const nextIndex =
				(currentIndex + direction + items.length) % items.length;

			const targetItem = items[nextIndex];

			if (targetItem) {
				targetItem.focus();
				targetItem.click();
			}
		};
	}

	/**
	 * Enregistre l'écouteur pour l'événement de sélection d'un item.
	 *
	 * @remarks
	 * Déclenche {@link #_onItemSelectedAction} lorsqu'un item emet l'événement
	 * `HTMLBnumSegmentedItem.Events.SELECTED`.
	 *
	 * @private
	 * @decorator `@Listen(HTMLBnumSegmentedItem.Events.SELECTED, { selector: TAG_SEGMENTED_ITEM })`
	 * @returns Fonction de gestion de la sélection d'item.
	 */
	@Listen(HTMLBnumSegmentedItem.Events.SELECTED, {
		selector: TAG_SEGMENTED_ITEM,
	})
	#_onItemSelected(): Function {
		return this._onItemSelectedAction;
	}

	/**
	 * Traite la sélection d'un item du contrôle segmenté.
	 *
	 * @description
	 * Désélectionne tous les items existants et sélectionne le nouvel item.
	 * Émet l'événement `bnum-segmented-control:change` avec les détails.
	 *
	 * @remarks
	 * - Valide que la cible de l'événement existe
	 * - Lève une erreur si la cible est manquante
	 * - Garantit qu'un seul item est sélectionné à la fois
	 *
	 * @private
	 * @decorator `@Fire('bnum-segmented-control:change')`
	 * @param e - Événement de sélection d'item depuis `HTMLBnumSegmentedItem`.
	 * @returns Détails de l'événement émis : `{value: string, item: HTMLBnumSegmentedItem, caller: HTMLBnumSegmentedControl}`
	 *
	 * @fires bnum-segmented-control:change
	 *
	 * @example
	 * ```typescript
	 * control.addEventListener('bnum-segmented-control:change', (e) => {
	 *   console.log('Item sélectionné:', e.detail.item);
	 *   console.log('Valeur:', e.detail.value);
	 * });
	 * ```
	 */
	@Fire(EVENT_CHANGE)
	private _onItemSelectedAction(
		e: CustomEvent<{
			innerEvent: Nullable<Event>;
			target: Nullable<HTMLBnumSegmentedItem>;
		}>,
	) {
		const { target } = e.detail;

		if (!target) {
			this._onError(
				new Error("Élément cible manquant dans l'événement sélectionné."),
			);
			return;
		}

		this.#_unselectAllItems();
		this.#_selectItem(target!);

		return { value: target?.value, item: target, caller: this };
	}

	/**
	 * Traite les erreurs internes du composant.
	 *
	 * @remarks
	 * - Enregistre l'erreur dans les logs
	 * - Émet l'événement `bnum-segmented-control:error`
	 * - Peut être déclenché lors de la sélection d'items invalides
	 *
	 * @private
	 * @decorator `@Fire('bnum-segmented-control:error')`
	 * @param error - L'erreur survenue.
	 * @returns Détails de l'événement d'erreur : `{error: Error, caller: HTMLBnumSegmentedControl}`
	 *
	 * @fires bnum-segmented-control:error
	 *
	 * @example
	 * ```typescript
	 * control.addEventListener('bnum-segmented-control:error', (e) => {
	 *   console.error('Erreur dans le contrôle:', e.detail.error);
	 * });
	 * ```
	 */
	@Fire(EVENT_ERROR)
	private _onError(error: Error) {
		Log.error('HTMLBnumSegmentedControl', 'Une erreur est survenue', error);
		return { error, caller: this };
	}

	/**
	 * Désélectionne tous les items du contrôle segmenté.
	 *
	 * @remarks
	 * Supprime l'attribut `selected` de tous les items, indépendamment de leur
	 * état actuel.
	 *
	 * @private
	 */
	#_unselectAllItems(): void {
		const items = this.querySelectorAll(TAG_SEGMENTED_ITEM);

		if (items.length > 0) {
			for (const element of Array.from(items)) {
				element.removeAttribute('selected');
			}
		}
	}

	/**
	 * Sélectionne un item spécifique du contrôle segmenté.
	 *
	 * @remarks
	 * Ajoute l'attribut `selected="true"` à l'item fourni.
	 * Doit être appelé après {@link #_unselectAllItems} pour respecter
	 * le comportement de sélection unique.
	 *
	 * @private
	 * @param item - L'item à sélectionner.
	 */
	#_selectItem(item: HTMLBnumSegmentedItem): void {
		item.setAttribute('selected', 'true');
	}
	//#region Private methods

	//#region Static methods
	/**
	 * Crée un nouveau contrôle segmenté avec une légende.
	 *
	 * @remarks
	 * Fabrique statique pour créer rapidement un contrôle segmenté vide.
	 * Les items doivent être ajoutés manuellement après la création.
	 *
	 * @static
	 * @param legend - La légende du contrôle segmenté.
	 * @returns L'élément de contrôle segmenté créé.
	 *
	 * @example
	 * ```typescript
	 * const control = HTMLBnumSegmentedControl.Create('Choisir une option');
	 * document.body.appendChild(control);
	 * ```
	 */
	static Create(legend: string): HTMLBnumSegmentedControl {
		const node = document.createElement(this.TAG) as HTMLBnumSegmentedControl;
		node.textContent = legend;
		return node;
	}

	/**
	 * Récupère l'énumération des événements du composant.
	 *
	 * @remarks
	 * Contient les noms des événements personnalisés émis par le composant.
	 * Utiliser ces constantes plutôt que des chaînes de caractères brutes
	 * pour éviter les erreurs typographiques.
	 *
	 * @example
	 * ```typescript
	 * const control = document.querySelector('bnum-segmented-control');
	 *
	 * // Bonne pratique : utiliser Events
	 * control.addEventListener(
	 *   HTMLBnumSegmentedControl.Events.CHANGE,
	 *   (e) => console.log('Changement:', e.detail)
	 * );
	 *
	 * // À éviter : chaîne brute
	 * control.addEventListener('bnum-segmented-control:change', (e) => {});
	 * ```
	 */
	static get Events() {
		return EVENTS;
	}

	//#endregion Static methods
}
